<template>
  <div v-if="area" class="ia-edit">
    <!-- Name -->
    <div class="ia-field">
      <label class="ia-label" for="ia-name">Zone name</label>
      <div class="ia-name-row">
        <input id="ia-name" v-model="editName" class="ia-input" placeholder="Area name" @keyup.enter="rename" />
        <button type="button" class="ia-save" :disabled="!nameChanged || !editName.trim()" @click="rename">
          <v-icon size="18">mdi-content-save-outline</v-icon><span>Save</span>
        </button>
      </div>
    </div>

    <div class="ia-divider"></div>

    <!-- Manage: settings-style rows (icon + description + action) -->
    <div class="ia-rows">
      <div v-if="allowTransform" class="ia-row">
        <span class="ia-row-icon"><v-icon size="20" color="#66c7b4">mdi-axis-arrow</v-icon></span>
        <span class="ia-row-text">
          <span class="ia-row-title">Move / resize</span>
          <span class="ia-row-sub">Reposition or resize this zone's bounds in the 3D view.</span>
        </span>
        <button type="button" class="ia-row-btn" @click="transformInspectionArea">Adjust</button>
      </div>

      <div class="ia-row ia-row--danger">
        <span class="ia-row-icon is-danger"><v-icon size="20" color="#ff8a80">mdi-delete-outline</v-icon></span>
        <span class="ia-row-text">
          <span class="ia-row-title">Delete zone</span>
          <span class="ia-row-sub">Permanently remove this inspection zone. Markers and hotspots are not deleted.</span>
        </span>
        <template v-if="!confirmingDelete">
          <button type="button" class="ia-row-btn is-danger" @click="confirmingDelete = true">Delete</button>
        </template>
        <span v-else class="ia-confirm-inline">
          <button type="button" class="ia-ghost" @click="confirmingDelete = false">Cancel</button>
          <button type="button" class="ia-danger" @click="remove">Confirm</button>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InspectionAreaDetails",
  emits: ["transformInspectionArea", "renameInspectionArea", "deleteInspectionArea"],
  props: {
    area: {
      type: Object,
      default: null
    },
    // Move/resize is a precise-pointer, 3D action — desktop only, never on touch.
    allowTransform: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editName: '',
      confirmingDelete: false,
    };
  },
  computed: {
    nameChanged() {
      return !!this.area && this.editName.trim() !== (this.area.name ?? '');
    }
  },
  watch: {
    // Reset the local edit state whenever a different area is opened.
    area: {
      immediate: true,
      handler(area) {
        this.editName = area?.name ?? '';
        this.confirmingDelete = false;
      }
    }
  },
  methods: {
    transformInspectionArea() {
      this.$emit("transformInspectionArea", this.area);
    },
    rename() {
      const name = this.editName.trim();
      if (!name || name === (this.area?.name ?? '')) return;
      this.$emit("renameInspectionArea", name);
    },
    remove() {
      this.confirmingDelete = false;
      this.$emit("deleteInspectionArea", this.area);
    }
  }
}
</script>

<style scoped>
.ia-edit {
  display: flex;
  flex-direction: column;
  gap: 18px;
  color: #fff;
}

.ia-field { display: flex; flex-direction: column; }

.ia-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
  margin-bottom: 8px;
}

.ia-name-row {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.ia-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 46px;
  padding: 0 14px;
  border-radius: 10px;
  color: #fff;
  font: inherit;
  font-size: 1rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: border-color 0.15s ease, background 0.15s ease;
}
.ia-input:focus { outline: none; border-color: rgba(100, 255, 218, 0.5); background: rgba(255, 255, 255, 0.06); }
.ia-input::placeholder { color: #7f939d; }

.ia-save {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 18px;
  height: 46px;
  border-radius: 10px;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  background: var(--duallo-accent-gradient);
  color: #071514;
  border: none;
}
.ia-save:disabled { opacity: 0.4; cursor: not-allowed; }

.ia-divider { height: 1px; background: rgba(255, 255, 255, 0.08); }

.ia-rows { display: flex; flex-direction: column; gap: 12px; }

/* Settings-style row: icon chip · title+description · action on the right. Fills the window width. */
.ia-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.ia-row--danger { background: rgba(239, 68, 68, 0.05); border-color: rgba(239, 68, 68, 0.16); }

.ia-row-icon {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: rgba(102, 199, 180, 0.1);
  border: 1px solid rgba(102, 199, 180, 0.22);
}
.ia-row-icon.is-danger { background: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.25); }

.ia-row-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ia-row-title { font-size: 0.95rem; font-weight: 700; color: #fff; }
.ia-row-sub { font-size: 0.82rem; color: #8aa0aa; line-height: 1.4; }

.ia-row-btn {
  flex: 0 0 auto;
  min-width: 96px;
  height: 40px;
  padding: 0 18px;
  border-radius: 10px;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  color: #e6edf0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.14);
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.ia-row-btn:hover { background: rgba(100, 255, 218, 0.1); border-color: rgba(100, 255, 218, 0.4); color: #fff; }
.ia-row-btn.is-danger { color: #ff9c9c; background: rgba(239, 68, 68, 0.12); border-color: rgba(239, 68, 68, 0.4); }
.ia-row-btn.is-danger:hover { background: rgba(239, 68, 68, 0.22); color: #ffb4b4; }

.ia-confirm-inline { flex: 0 0 auto; display: inline-flex; gap: 8px; }
.ia-ghost, .ia-danger {
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.ia-ghost { background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.14); color: #cfd8dc; }
.ia-danger { background: rgba(239, 68, 68, 0.2); border: 1px solid rgba(239, 68, 68, 0.45); color: #f87171; }
.ia-danger:hover { background: rgba(239, 68, 68, 0.32); }

/* Narrow (mobile sheet / 95vw overlay on phones): stack the action under the text. */
@media (max-width: 560px) {
  .ia-row { flex-wrap: wrap; }
  .ia-row-text { flex: 1 1 100%; order: 2; }
  .ia-row-icon { order: 1; }
  .ia-row-btn, .ia-confirm-inline { order: 3; flex: 1 1 100%; margin-left: 54px; }
  .ia-confirm-inline > * { flex: 1 1 0; }
}
</style>
